import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { LookbookState, ViewportState, saveProjectLookbookState, loadProjectLookbookState } from '../lib/sessions';

// Generation item on the canvas
export interface LookbookGeneration {
  id: string | number;
  x: number;
  y: number;
  width: number;
  height: number;
  img?: string;
  src?: string;
  prompt?: string;
  model?: string;
  type?: string;
  pan_x?: number;
  pan_y?: number;
  is_locked?: boolean;
  is_public?: boolean;
  is_favorited?: boolean;
  aspectRatio?: number;
  settings?: any;
  isLoading?: boolean;
  // Text bubble properties
  text_content?: string;
  bubble_style?: string;
  font_family?: string;
  text_color?: string;
  bubble_color?: string;
  text_align?: string;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface LookbookStore {
  // State
  generations: LookbookGeneration[];
  viewport: ViewportState | null;
  galleryScale: number;
  projectId: string | null;
  saveStatus: SaveStatus;
  isLoading: boolean;
  isDirty: boolean;

  // Internal tracking
  _lastSavedState: string | null;
  _saveTimeout: NodeJS.Timeout | null;

  // Actions
  setGenerations: (generations: LookbookGeneration[] | ((prev: LookbookGeneration[]) => LookbookGeneration[])) => void;
  updateGeneration: (id: string | number, updates: Partial<LookbookGeneration>) => void;
  addGeneration: (generation: LookbookGeneration) => void;
  removeGeneration: (id: string | number) => void;
  setViewport: (viewport: ViewportState) => void;
  setGalleryScale: (scale: number) => void;

  // Project management
  loadProject: (projectId: string) => Promise<void>;
  setProjectId: (projectId: string | null) => Promise<void>; // Set projectId and load galleryScale
  clearProject: () => void;

  // Save management
  saveNow: () => Promise<void>;
  markDirty: () => void;

  // Status setters (for external sync with Hero.tsx)
  setSaveStatus: (status: SaveStatus) => void;
  setIsDirty: (dirty: boolean) => void;
}

// Debounce helper
const SAVE_DEBOUNCE_MS = 2000;

export const useLookbookStore = create<LookbookStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    generations: [],
    viewport: null,
    galleryScale: 3, // Default gallery scale
    projectId: null,
    saveStatus: 'idle',
    isLoading: false,
    isDirty: false,
    _lastSavedState: null,
    _saveTimeout: null,

    // Set generations (supports functional updates like React setState)
    setGenerations: (generations) => {
      set(state => ({
        generations: typeof generations === 'function'
          ? generations(state.generations)
          : generations,
        isDirty: true
      }));
      get().markDirty();
    },

    // Update a single generation
    updateGeneration: (id, updates) => {
      set(state => ({
        generations: state.generations.map(g =>
          g.id === id ? { ...g, ...updates } : g
        ),
        isDirty: true
      }));
      get().markDirty();
    },

    // Add a generation
    addGeneration: (generation) => {
      set(state => ({
        generations: [...state.generations, generation],
        isDirty: true
      }));
      get().markDirty();
    },

    // Remove a generation
    removeGeneration: (id) => {
      set(state => ({
        generations: state.generations.filter(g => g.id !== id),
        isDirty: true
      }));
      get().markDirty();
    },

    // Set viewport
    setViewport: (viewport) => {
      set({ viewport, isDirty: true });
      get().markDirty();
    },

    // Set gallery scale
    setGalleryScale: (scale) => {
      set({ galleryScale: scale, isDirty: true });
      get().markDirty();
    },

    // Load a project's lookbook state
    loadProject: async (projectId) => {
      const state = get();

      // Save current project before switching (if dirty)
      if (state.projectId && state.isDirty && state.generations.length > 0) {
        await state.saveNow();
      }

      set({ isLoading: true, projectId });

      try {
        const lookbookState = await loadProjectLookbookState(projectId);

        set({
          generations: lookbookState?.generations || [],
          viewport: lookbookState?.viewport || null,
          galleryScale: lookbookState?.galleryScale ?? 3,
          isLoading: false,
          isDirty: false,
          saveStatus: 'idle',
          _lastSavedState: JSON.stringify({
            generations: lookbookState?.generations || [],
            viewport: lookbookState?.viewport || null,
            galleryScale: lookbookState?.galleryScale ?? 3
          })
        });
      } catch (error) {
        console.error('Failed to load lookbook state:', error);
        set({
          generations: [],
          viewport: null,
          isLoading: false,
          isDirty: false,
          saveStatus: 'error'
        });
      }
    },

    // Set project ID only (enables save without loading full lookbook state)
    // Also loads galleryScale if available to prevent glitches on page refresh
    setProjectId: async (projectId) => {
      if (!projectId) {
        set({ projectId: null });
        return;
      }

      // Set projectId immediately
      set({ projectId });

      // Load galleryScale from project's lookbook_state if available
      try {
        const lookbookState = await loadProjectLookbookState(projectId);
        if (lookbookState?.galleryScale !== undefined) {
          set({ galleryScale: lookbookState.galleryScale });
        }
      } catch (e) {
        console.error('Failed to load galleryScale:', e);
      }
    },

    // Clear project (when no project selected)
    clearProject: () => {
      const state = get();

      // Clear any pending save
      if (state._saveTimeout) {
        clearTimeout(state._saveTimeout);
      }

      set({
        generations: [],
        viewport: null,
        galleryScale: 3,
        projectId: null,
        isDirty: false,
        saveStatus: 'idle',
        _lastSavedState: null,
        _saveTimeout: null
      });
    },

    // Save immediately
    saveNow: async () => {
      const state = get();

      if (!state.projectId) return;

      // Clear any pending debounced save
      if (state._saveTimeout) {
        clearTimeout(state._saveTimeout);
        set({ _saveTimeout: null });
      }

      // Check if state actually changed
      const currentState = JSON.stringify({
        generations: state.generations,
        viewport: state.viewport,
        galleryScale: state.galleryScale
      });

      if (currentState === state._lastSavedState) {
        set({ isDirty: false, saveStatus: 'saved' });
        return;
      }

      set({ saveStatus: 'saving' });

      try {
        await saveProjectLookbookState(state.projectId, {
          layout_data: [],
          image_ids: state.generations.map(g => String(g.id)),
          generations: state.generations,
          viewport: state.viewport || undefined,
          galleryScale: state.galleryScale
        });

        set({
          saveStatus: 'saved',
          isDirty: false,
          _lastSavedState: currentState
        });

        // Reset to idle after 2 seconds
        setTimeout(() => {
          if (get().saveStatus === 'saved') {
            set({ saveStatus: 'idle' });
          }
        }, 2000);
      } catch (error) {
        console.error('Failed to save lookbook state:', error);
        set({ saveStatus: 'error' });
      }
    },

    // Mark as dirty and schedule save
    markDirty: () => {
      const state = get();

      if (!state.projectId) return;

      // Clear existing timeout
      if (state._saveTimeout) {
        clearTimeout(state._saveTimeout);
      }

      // Schedule new save
      const timeout = setTimeout(() => {
        get().saveNow();
      }, SAVE_DEBOUNCE_MS);

      set({ _saveTimeout: timeout, isDirty: true });
    },

    // Status setters (for external sync with Hero.tsx)
    setSaveStatus: (status) => set({ saveStatus: status }),
    setIsDirty: (dirty) => set({ isDirty: dirty })
  }))
);

// Selector hooks for common patterns
export const useGenerations = () => useLookbookStore(state => state.generations);
export const useViewport = () => useLookbookStore(state => state.viewport);
export const useGalleryScale = () => useLookbookStore(state => state.galleryScale);
export const useSaveStatus = () => useLookbookStore(state => state.saveStatus);
export const useIsDirty = () => useLookbookStore(state => state.isDirty);
export const useIsLoading = () => useLookbookStore(state => state.isLoading);

// Actions hook (stable references)
export const useLookbookActions = () => useLookbookStore(state => ({
  setGenerations: state.setGenerations,
  updateGeneration: state.updateGeneration,
  addGeneration: state.addGeneration,
  removeGeneration: state.removeGeneration,
  setViewport: state.setViewport,
  setGalleryScale: state.setGalleryScale,
  loadProject: state.loadProject,
  clearProject: state.clearProject,
  saveNow: state.saveNow
}));
